import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Lock } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import {
  buildContentTrackingMetadata,
  getApiBaseUrl,
} from "../lib/gatedContentTracking";

const BlogGate = ({ onAccessGranted, contentType = "article" }) => {
  const isVideo = contentType === "video";
  const trackingContentType = isVideo ? "video" : "blog";

  const [email, setEmail] = useState("");
  const [isAgreed, setIsAgreed] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const heading = isVideo
    ? "Enter your email to view the video"
    : "Enter your email to read this article";
  const submitLabel = isVideo ? "View video" : "Read article";
  const description = isVideo
    ? "Videos on the Asha Health blog are available to verified dental professionals."
    : "Articles on the Asha Health blog are available to our leads and customers.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (isVideo && !isAgreed) {
      setError(
        "Please confirm the attestation to continue."
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = email.trim().toLowerCase();

    if (!emailRegex.test(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);

    try {
      const apiUrl = getApiBaseUrl();
      const contentMetadata = buildContentTrackingMetadata({
        contentType: trackingContentType,
      });

      const response = await fetch(`${apiUrl}/api/blog/access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: normalizedEmail,
          ...contentMetadata,
          ...(isVideo && {
            ndaSigned: true,
            privacyPolicySigned: true,
            dentalAttestation: true,
          }),
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error("Server returned an invalid response. Please try again.");
      }

      if (!response.ok) {
        throw new Error(
          data.error || `Server error: ${response.status} ${response.statusText}`
        );
      }

      setIsSuccess(true);

      setTimeout(() => {
        onAccessGranted(normalizedEmail, data.sessionId, {
          videoAttestation: isVideo ? true : undefined,
        });
      }, 600);
    } catch (err) {
      console.error("BlogGate error:", err);
      if (err instanceof TypeError && err.message.includes("fetch")) {
        setError(
          "Unable to connect to server. Please check your connection and try again."
        );
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-neutral-950/90 backdrop-blur-md p-4"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[120px]" />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key="email-form"
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: -20, opacity: 0 }}
          transition={{ type: "spring", duration: 0.8 }}
          className="relative w-full max-w-md bg-neutral-900/50 border border-white/10 rounded-3xl p-8 shadow-2xl overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none" />

          <div className="relative z-10 flex flex-col items-center text-center">
            <div className="mb-6 p-4 rounded-full bg-blue-500/10 border border-blue-500/20 shadow-[0_0_20px_rgba(59,130,246,0.15)]">
              <Lock className="w-8 h-8 text-blue-400" />
            </div>

            <h2 className="text-2xl font-bold text-white mb-2">
              {heading}
            </h2>
            <p className="text-neutral-400 mb-8 leading-relaxed text-sm">
              {description}
            </p>

            <form onSubmit={handleSubmit} className="w-full space-y-6">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-neutral-800/50 border-white/10 text-white placeholder:text-neutral-500 focus:border-blue-500/50 focus:ring-blue-500/20 h-12 rounded-xl transition-all"
                  disabled={isLoading || isSuccess}
                  autoFocus
                />
              </div>

              {isVideo && (
                <div className="flex items-start gap-3 text-left">
                  <Checkbox
                    id="video-attestation"
                    checked={isAgreed}
                    onCheckedChange={setIsAgreed}
                    className="mt-1 border-white/30 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <label
                    htmlFor="video-attestation"
                    className="text-sm text-neutral-400 leading-snug cursor-pointer select-none"
                  >
                    By viewing, I accept the Asha Health{" "}
                    <a
                      href="/nda"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
                    >
                      NDA
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
                    >
                      Privacy Policy
                    </a>
                    , and attest that I am a dentist or representing a dental practice.
                  </label>
                </div>
              )}

              {error && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="text-red-400 text-sm text-center bg-red-500/10 p-3 rounded-lg leading-relaxed"
                >
                  {error}
                </motion.p>
              )}

              <Button
                type="submit"
                className={`w-full h-12 text-lg font-semibold rounded-xl transition-all duration-300 ${
                  isSuccess
                    ? "bg-green-500 hover:bg-green-600 text-white"
                    : "bg-blue-600 hover:bg-blue-500 text-white hover:shadow-[0_0_20px_rgba(37,99,235,0.3)]"
                }`}
                disabled={isLoading || isSuccess || !email || (isVideo && !isAgreed)}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Verifying...</span>
                  </div>
                ) : isSuccess ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    <span>Access granted</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{submitLabel}</span>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

export default BlogGate;
